import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode
} from "react";

import { createAppAudioRuntime } from "../../audio/app-catalog";
import type { AudioRuntime } from "../../audio/runtime";
import { AudioContext, type AudioCommands } from "./context";

const FIRST_GESTURE_EVENTS = [
  "pointerup",
  "touchend",
  "keydown",
  "click"
] as const;

export type AudioRuntimeFactory = () => AudioRuntime;

export interface AudioProviderProps {
  readonly children: ReactNode;
  readonly createRuntime?: AudioRuntimeFactory;
  readonly firstGestureTarget?: EventTarget | null;
}

export function AudioProvider({
  children,
  createRuntime,
  firstGestureTarget
}: AudioProviderProps) {
  const [runtime] = useState<AudioRuntime>(() =>
    (createRuntime ?? createAppAudioRuntime)()
  );
  const lifecycleVersion = useRef(0);

  const subscribeRuntime = useCallback(
    (notify: () => void) => runtime.subscribe(notify),
    [runtime]
  );
  const getRuntimeSnapshot = useCallback(() => runtime.getSnapshot(), [runtime]);
  const subscribePreferences = useCallback(
    (notify: () => void) => runtime.preferencesStore.subscribe(notify),
    [runtime]
  );
  const getPreferencesSnapshot = useCallback(
    () => runtime.getPreferences(),
    [runtime]
  );

  const snapshot = useSyncExternalStore(
    subscribeRuntime,
    getRuntimeSnapshot,
    getRuntimeSnapshot
  );
  const preferences = useSyncExternalStore(
    subscribePreferences,
    getPreferencesSnapshot,
    getPreferencesSnapshot
  );

  const commands = useMemo<AudioCommands>(
    () => ({
      unlock: () => runtime.unlock(),
      playMusic: (trackId) => runtime.playMusic(trackId),
      playVoice: (trackId) => runtime.playVoice(trackId),
      replayVoice: () => runtime.replayVoice(),
      playSfx: (trackId) => runtime.playSfx(trackId),
      stopMusic: () => runtime.stopMusic(),
      stopVoice: (playbackId) => runtime.stopVoice(playbackId),
      stopAll: () => runtime.stopAll(),
      setMuted: (muted) => runtime.setMuted(muted),
      toggleMuted: () => runtime.toggleMuted(),
      setMasterVolume: (volume) => runtime.setMasterVolume(volume),
      setChannelVolume: (channel, volume) =>
        runtime.setChannelVolume(channel, volume)
    }),
    [runtime]
  );

  useEffect(() => {
    if (snapshot.unlockState === "unlocked") {
      return;
    }

    const target =
      firstGestureTarget === undefined
        ? typeof document === "undefined"
          ? null
          : document
        : firstGestureTarget;
    if (!target) {
      return;
    }

    const handleFirstGesture: EventListener = () => {
      void runtime.unlock();
    };
    const detach = () => {
      for (const eventName of FIRST_GESTURE_EVENTS) {
        target.removeEventListener(eventName, handleFirstGesture, true);
      }
    };

    for (const eventName of FIRST_GESTURE_EVENTS) {
      target.addEventListener(eventName, handleFirstGesture, true);
    }

    return detach;
  }, [firstGestureTarget, runtime, snapshot.unlockState]);

  useEffect(() => {
    const mountedVersion = lifecycleVersion.current + 1;
    lifecycleVersion.current = mountedVersion;

    return () => {
      queueMicrotask(() => {
        if (lifecycleVersion.current === mountedVersion) {
          runtime.destroy();
        }
      });
    };
  }, [runtime]);

  const value = useMemo(
    () => ({ snapshot, preferences, commands }),
    [commands, preferences, snapshot]
  );

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
}
